import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, ErrorState, Icon, Skeleton, toast, type BadgeTone, useCopy } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { CardActions } from "./panel-kit";

type EgressStatus = "healthy" | "degraded" | "unconfigured";

const STATUS_TONE: Record<EgressStatus, BadgeTone> = { healthy: "success", degraded: "warning", unconfigured: "neutral" };

/** `GET /v1/network/egress-ips` (M29 E29): the published fixed outbound addresses and the health of the path. */
function useEgressIps() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: ["ws", wsId, "settings", "egress-ips"] as const, queryFn: () => unwrap(api.GET("/v1/network/egress-ips")) });
}

function CardHeader({ status }: { status: EgressStatus | undefined }) {
  const { t } = useTranslation();
  return (
    <div className="card-header flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 id="egress-title" className="card-title">
          {t("privacyM29.egress.title")}
        </h2>
        <p className="mt-1 text-xs text-fg-muted">{t("privacyM29.egress.intro")}</p>
      </div>
      {status ? (
        <Badge tone={STATUS_TONE[status]} dot data-testid="egress-status">
          {t(`privacyM29.egress.status.${status}`)}
        </Badge>
      ) : null}
    </div>
  );
}

function AddressRow({ address, region }: { address: string; region: string | null | undefined }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  return (
    <li className="flex items-center justify-between gap-3 px-5 py-3">
      <div className="min-w-0">
        <code className="break-all font-mono text-sm font-semibold" data-testid="egress-address">
          {address}
        </code>
        {region ? <div className="text-xs text-fg-muted">{t("privacyM29.egress.region", { region })}</div> : null}
      </div>
      <Button
        variant="secondary"
        aria-label={t("privacyM29.egress.copyOne", { address })}
        onClick={() => {
          void copy(address).then((ok) => {
            if (ok) toast.success(t("privacyM29.egress.copiedOne", { address }));
            else toast.error(t("settings.copyFailed"));
          });
        }}
      >
        <Icon name={copied ? "check" : "copy"} />
      </Button>
    </li>
  );
}

function VerifiedLine({ verifiedAt }: { verifiedAt: string | null | undefined }) {
  const { t, i18n } = useTranslation();
  if (!verifiedAt) return <span data-testid="egress-verified">{t("privacyM29.egress.neverVerified")}</span>;
  const when = new Intl.DateTimeFormat(i18n.language === "fil" ? "fil-PH" : "en-PH", { dateStyle: "medium", timeStyle: "short" }).format(new Date(verifiedAt));
  return (
    <time dateTime={verifiedAt} data-testid="egress-verified">
      {t("privacyM29.egress.verified", { when })}
    </time>
  );
}

function CardLoading() {
  const { t } = useTranslation();
  return (
    <section className="card" aria-labelledby="egress-title" data-testid="egress-card" aria-busy="true">
      <CardHeader status={undefined} />
      <div className="space-y-3 px-5 py-4" role="status" aria-label={t("common.loading")}>
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-1/4" />
      </div>
    </section>
  );
}

/** Settings > Privacy > "Outbound IP addresses" (privacy-security pin 3, PDI-005): the addresses to put on a firewall allowlist. */
export function EgressIpsCard() {
  const { t } = useTranslation();
  const { copied, copy } = useCopy();
  const egress = useEgressIps();
  if (egress.isPending) return <CardLoading />;
  if (egress.isError) {
    return (
      <section className="card" aria-labelledby="egress-title" data-testid="egress-card">
        <CardHeader status={undefined} />
        <ErrorState headingLevel={3} title={t("privacyM29.egress.failedTitle")} description={t("privacyM29.egress.failedBody")} onRetry={() => void egress.refetch()} retryLabel={t("common.retry")} />
      </section>
    );
  }
  const { status, ips, last_verified_at: verifiedAt, webhooks_use_egress: webhooks } = egress.data;
  const copyAll = () => {
    void copy(ips.map((ip) => ip.address).join("\n")).then((ok) => {
      if (ok) toast.success(t("privacyM29.egress.copiedAll"));
      else toast.error(t("settings.copyFailed"));
    });
  };
  return (
    <section className="card" aria-labelledby="egress-title" data-testid="egress-card">
      <CardHeader status={status} />
      {ips.length === 0 ? (
        <EmptyState
          icon="globe"
          headingLevel={3}
          title={t("privacyM29.egress.emptyTitle")}
          description={t("privacyM29.egress.emptyBody")}
          action={
            <Button variant="secondary" loading={egress.isFetching} onClick={() => void egress.refetch()}>
              <Icon name="refresh-cw" />
              {t("privacyM29.egress.recheck")}
            </Button>
          }
        />
      ) : (
        <>
          {status === "degraded" ? (
            <div className="flex items-start gap-2 border-b border-line bg-warning-soft px-5 py-3 text-xs text-fg" role="note" data-testid="egress-degraded">
              <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>
                <strong className="font-semibold">{t("privacyM29.egress.degradedTitle")}.</strong> {t("privacyM29.egress.degradedBody")}
              </span>
            </div>
          ) : null}
          <ul className="divide-y divide-line" aria-label={t("privacyM29.egress.listLabel")}>
            {ips.map((ip) => (
              <AddressRow key={ip.address} address={ip.address} region={ip.region} />
            ))}
          </ul>
          <div className="flex items-start gap-2 border-t border-line px-5 py-3 text-xs text-fg-muted">
            <Icon name="info" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
            <span data-testid="egress-webhooks">{webhooks ? t("privacyM29.egress.webhooksOn") : t("privacyM29.egress.webhooksOff")}</span>
          </div>
          <CardActions>
            <span className="mr-auto self-center text-xs text-fg-muted">
              <VerifiedLine verifiedAt={verifiedAt} />
            </span>
            <Button variant="secondary" onClick={copyAll}>
              <Icon name={copied ? "check" : "copy"} />
              {t("privacyM29.egress.copyAll")}
            </Button>
          </CardActions>
        </>
      )}
    </section>
  );
}
